import { Platform } from 'react-native';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import * as FileSystem from 'expo-file-system/legacy';
import { ProductImageValidationError } from '../domain/productImageErrors';

export type CatalogImageStorageFormat = 'webp' | 'jpeg';

export type OptimizedProductImage = {
  base64: string;
  uri: string;
};

export type OptimizedProductImages = {
  storageFormat: CatalogImageStorageFormat;
  thumb: OptimizedProductImage;
  medium: OptimizedProductImage;
  full: OptimizedProductImage;
};

const SIZES = {
  thumb: { width: 96, height: 96, quality: 0.7 },
  medium: { width: 512, height: 512, quality: 0.76 },
  /** Portal hero ~512px × 2 DPR; re-subir foto en catálogo para regenerar variantes. */
  full: { width: 1200, height: 1200, quality: 0.82 },
} as const;

function saveFormatForPlatform(): SaveFormat {
  return Platform.OS === 'web' ? SaveFormat.JPEG : SaveFormat.WEBP;
}

function storageFormatForPlatform(): CatalogImageStorageFormat {
  return Platform.OS === 'web' ? 'jpeg' : 'webp';
}

function isWebpEncoderUnsupported(message: string): boolean {
  const lower = message.toLowerCase();
  return lower.includes('image/webp') && lower.includes('does not support encoding');
}

async function readFileAsBase64(uri: string): Promise<string> {
  if (Platform.OS === 'web') {
    const response = await fetch(uri);
    if (!response.ok) {
      throw new Error('No se pudo leer la imagen optimizada.');
    }
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const commaIdx = result.indexOf(',');
          resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
        } else {
          reject(new Error('No se pudo procesar la imagen optimizada.'));
        }
      };
      reader.onerror = () => reject(new Error('Error al leer la imagen optimizada.'));
      reader.readAsDataURL(blob);
    });
  }
  return FileSystem.readAsStringAsync(uri, { encoding: 'base64' as const });
}

async function generateSize(
  uri: string,
  size: { width: number; height: number; quality: number },
  format: SaveFormat,
): Promise<OptimizedProductImage> {
  const result = await manipulateAsync(
    uri,
    [{ resize: { width: size.width } }],
    {
      format,
      compress: size.quality,
      base64: true,
    },
  );

  const base64 = result.base64 ?? (await readFileAsBase64(result.uri));
  return { base64, uri: result.uri };
}

export async function optimizeProductImage(uri: string): Promise<OptimizedProductImages> {
  const primaryFormat = saveFormatForPlatform();
  const storageFormat = storageFormatForPlatform();

  try {
    const [thumb, medium, full] = await Promise.all([
      generateSize(uri, SIZES.thumb, primaryFormat),
      generateSize(uri, SIZES.medium, primaryFormat),
      generateSize(uri, SIZES.full, primaryFormat),
    ]);
    return { storageFormat, thumb, medium, full };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'error desconocido';
    if (primaryFormat === SaveFormat.WEBP && isWebpEncoderUnsupported(message)) {
      throw new ProductImageValidationError(
        'Tu navegador no puede preparar WebP. Probá desde la app o subí JPG/PNG.',
      );
    }
    throw new Error(`No se pudo optimizar la imagen: ${message}`);
  }
}
